/**
 * GlobalAdminMfaCallout — editorial operational-risk callout for the
 * Identity / User Analytics Overview tab.
 *
 * Surfaces the single named risk operators care about most: Global Admins
 * without MFA. Renders only when the count is > 0 — silence is golden when
 * the gap doesn't exist.
 *
 * Voice contract (CLAUDE.md §Product Voice §1-6):
 *   - Verdict ≤ 6 words, italic, names the subject.
 *   - One short prescription sentence. Period-then-clause, no em-dash.
 *   - Named subjects ("Global Admins"), not row counts.
 *   - ALL CAPS micro-text for the kicker.
 *
 * Visual reference: Standfirst.tsx kicker pill + italic-mix headline pattern.
 * Tone: operational risk (sand/red borderLeft), NOT the calm identity pillar
 * tone — this is meant to read as "act on this today", not "here's a metric".
 *
 * Per memory `feedback_brief_card_score_visual`, the score-box visual is
 * reserved for composite-pillar pages — this callout deliberately does NOT
 * replicate that chrome. It's a focused inline aside, not a brief.
 */

import { Link } from 'react-router-dom'
import { ShieldExclamationIcon } from '@heroicons/react/24/outline'

interface Props {
  /** Count of enabled member Global Admins without MFA. From
   *  `UserInsightsSummary.noMfaGlobalAdmins`. */
  count: number
}

/** 1-9 → word form, 10+ → numerals. Matches the convention in
 *  identity-brief.ts / posture-brief.ts so the voice stays consistent. */
function numberWord(n: number): string {
  const words = [
    'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  ]
  return words[n] ?? String(n)
}

export default function GlobalAdminMfaCallout({ count }: Props) {
  // Silence is golden — no gap, no callout.
  if (!count || count <= 0) return null

  // Subject-led headline. Word form for 1-9 (editorial), numerals for 10+.
  // Plural noun only when count !== 1. ≤ 6 words on the headline.
  // Headline is composed inline in the JSX below so the count can be
  // wrapped in <em> for italic emphasis without an extra parser.
  const subject = count === 1 ? 'Global Admin' : 'Global Admins'

  // Prescription sentence. Period-then-clause, no em-dash. Same dignity as
  // the canonical Operations panel phrasing.
  const prescription = "They're the highest-impact MFA gap. Enrol them today."

  return (
    <aside
      data-tone="identity"
      role="status"
      aria-live="polite"
      className="
        mb-7 grid grid-cols-1 items-center gap-4
        rounded-2xl border border-dark-600 bg-dark-800 px-6 py-5
        sm:grid-cols-[auto_1fr_auto]
      "
      style={{
        // Operational-risk tone — primary-red borderLeft, matches the
        // OperationsPanel critical-row treatment.
        borderLeft: '3px solid #E57373',
      }}
    >
      <ShieldExclamationIcon
        className="h-7 w-7 flex-shrink-0"
        style={{ color: '#E57373' }}
        aria-hidden
      />

      <div className="min-w-0">
        <div
          className="text-[10px] font-bold uppercase tracking-[0.22em]"
          style={{ color: '#E57373' }}
        >
          Operational risk · Identity
        </div>
        <h3
          className="mt-1.5 text-[20px] font-extrabold leading-tight tracking-[-0.015em] text-slate-100 dark:text-slate-100"
        >
          <em
            style={{
              fontStyle: 'italic',
              fontWeight: 600,
              color: '#E57373',
            }}
          >
            {numberWord(count)}
          </em>{' '}
          {subject} without MFA.
        </h3>
        <p className="mt-1.5 text-[13px] leading-[1.55] text-slate-400">
          {prescription}
        </p>
      </div>

      <Link
        to="/user-insights?tab=mfa&scope=globalAdmins"
        className="
          inline-flex items-center justify-center self-start
          rounded-full border border-dark-600 bg-dark-900 px-4 py-2
          text-[10px] font-bold uppercase tracking-[0.18em] text-slate-100
          transition-colors hover:border-current
          sm:self-center
        "
        style={{ color: '#E57373' }}
        aria-label={
          count === 1
            ? 'View the Global Admin without MFA'
            : `View the ${count} Global Admins without MFA`
        }
      >
        View these admins →
      </Link>

      {/* Hidden phrase variants kept inline for grep-friendly voice review.
          The component renders one verdict above; the alternates are recorded
          here so future maintainers see the bar without rummaging through
          history. Variants:
            "One Global Admin without MFA."
            "Two Global Admins without MFA."
            "Three Global Admins without MFA."
            "12 Global Admins without MFA." */}
      {/* headline variants stay above; this comment block is intentional. */}
    </aside>
  )
}
